import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/site/LegalPage";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — ClutchCube" },
      {
        name: "description",
        content:
          "How ClutchCube collects, uses, and protects information submitted through our website and investor communications.",
      },
      { property: "og:title", content: "Privacy Policy — ClutchCube" },
      {
        property: "og:description",
        content:
          "How ClutchCube collects, uses, and protects information submitted through our website and investor communications.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://clutchcube.com/privacy" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://clutchcube.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      subtitle="ClutchCube respects your privacy. This policy explains what information we collect, how we use it, and the choices you have."
      updated="2026"
    >
      <p>
        This Privacy Policy applies to ClutchCube.com and any related digital properties operated by
        ClutchCube (&ldquo;ClutchCube,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By using this website
        or submitting information through it, you agree to the terms described below.
      </p>

      <LegalSection title="1. Information We Collect">
        <p>We collect information you provide directly and information collected automatically as you use the site.</p>
        <p className="text-foreground font-medium mt-6">Information you provide:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Company, fund, or organization</li>
          <li>Investor category and estimated investment interest</li>
          <li>Messages, notes, or other content submitted through forms</li>
        </ul>
        <p className="text-foreground font-medium mt-6">Information collected automatically:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Website analytics and usage data</li>
          <li>Device, browser, and operating system information</li>
          <li>IP address and approximate location</li>
          <li>Referring URLs and pages visited</li>
        </ul>
      </LegalSection>

      <LegalSection title="2. How We Use Your Information">
        <ul className="list-disc pl-6 space-y-2">
          <li>Respond to investor inquiries and follow-up requests</li>
          <li>Deliver requested investor materials and updates</li>
          <li>Communicate about ClutchCube&rsquo;s products, business plans, and opportunities</li>
          <li>Operate, improve, and secure the website</li>
          <li>Analyze usage patterns to enhance user experience</li>
          <li>Detect, prevent, and address technical or security issues</li>
          <li>Comply with legal obligations</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Cookies and Analytics">
        <p>
          We use cookies and similar technologies to operate the website, remember preferences, and understand how visitors
          interact with our content. You can control cookies through your browser settings; disabling them may affect
          functionality.
        </p>
        <p>
          We use <span className="text-foreground font-medium">Google Analytics 4</span> (Google LLC) for anonymous
          behavioral measurement. Google Analytics sets cookies and collects aggregated, non-identifying information such as
          pages viewed, referring sources, campaign parameters (UTM tags), approximate location derived from a truncated IP
          address, device and browser type, and anonymous interaction events — for example when a visitor clicks an investor,
          partnership, or media call-to-action or submits a request form.
        </p>
        <p>
          We do <span className="text-foreground font-medium">not</span> send personal information to Google Analytics. Names,
          email addresses, phone numbers, company or fund names, message contents, private document links, and internal record
          identifiers are never included in analytics events. Analytics data is used only in aggregate to measure website
          performance and campaign effectiveness, and is not used to identify individual visitors.
        </p>
        <p>
          You may opt out of Google Analytics measurement using the{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Analytics Opt-out Browser Add-on
          </a>
          , browser tracking-protection settings, or standard content blockers. Blocking analytics does not affect your ability
          to use the website or submit a request.
        </p>
      </LegalSection>


      <LegalSection title="4. Email Communications">
        <p>
          When you contact us or request materials, we may send transactional emails (such as deck delivery and confirmations)
          and periodic updates related to ClutchCube. You may opt out of non-essential communications at any time by replying
          to the message or contacting us directly.
        </p>
      </LegalSection>

      <LegalSection title="5. Data Security">
        <p>
          We use reasonable administrative, technical, and physical safeguards designed to protect the information we collect.
          No method of transmission or storage is completely secure, and we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="6. Third-Party Services">
        <p>We rely on trusted service providers to operate our website and communications. These include:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="text-foreground font-medium">Resend</span> — transactional email delivery
          </li>
          <li>
            <span className="text-foreground font-medium">Supabase</span> — secure data storage and application backend
          </li>
          <li>
            <span className="text-foreground font-medium">Sentry</span> — error monitoring and reliability
          </li>
          <li>
            <span className="text-foreground font-medium">Google Analytics 4</span> — anonymous, aggregated website and
            campaign measurement
          </li>
        </ul>
        <p>
          Each provider processes information under its own terms and security controls. We only share the information
          necessary for the service to function.
        </p>
      </LegalSection>

      <LegalSection title="7. Your Privacy Rights">
        <p>
          Depending on your jurisdiction, you may have the right to access, correct, delete, or restrict the use of your
          personal information, and to object to certain processing. To exercise these rights, please contact us using the
          details below. We will respond within a reasonable timeframe as required by applicable law.
        </p>
      </LegalSection>

      <LegalSection title="8. Data Retention">
        <p>
          We retain information for as long as necessary to fulfill the purposes described in this policy, comply with legal
          and regulatory requirements, resolve disputes, and enforce our agreements. Investor communications may be retained
          for the duration of an active or prospective relationship.
        </p>
      </LegalSection>

      <LegalSection title="9. Children's Privacy">
        <p>
          ClutchCube.com is not directed to children under 13, and we do not knowingly collect personal information from
          children. If you believe a child has provided us with information, please contact us so we can delete it.
        </p>
      </LegalSection>

      <LegalSection title="10. International Visitors">
        <p>
          ClutchCube operates in the United States. If you access the website from outside the U.S., your information may be
          transferred to, stored, and processed in the United States or other jurisdictions where our providers operate. By
          using the site, you consent to this transfer.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated
          effective date. Your continued use of the website after changes take effect constitutes acceptance of the revised
          policy.
        </p>
      </LegalSection>

      <LegalSection title="12. Business Ownership">
        <p>
          ClutchCube is owned or operated by Colowebo LLC and is part of the VentureX OS portfolio of ventures.
          Operational, compliance, customer support, analytics, and administrative functions may be provided through
          VentureX OS systems.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact Us">
        <p>
          For questions about this Privacy Policy or our data practices, please contact us through the{" "}
          <a href="/#invest" className="text-primary hover:underline">
            investor contact form
          </a>{" "}
          on this website.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
